import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from 'antd';
import { useHomeData } from '../hooks/useHomeData';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useMainButton } from '../../../hooks/useMainButton';
import { useBookingFlowStore } from '../../../store/bookingFlowStore';
import { useClientTenantStore } from '../../../store/clientTenantStore';
import { usePullToRefresh } from '../../../hooks/usePullToRefresh';
import { useRefreshOnVisibility } from '../../../hooks/useRefreshOnVisibility';
import PullToRefreshIndicator from '../../../components/shared/PullToRefreshIndicator';
import { HomeScreenSkeleton } from './skeletons/HomeScreenSkeleton';
import { BusinessHero } from './hero/BusinessHero';
import { UpcomingBookingCard } from './upcoming/UpcomingBookingCard';
import { UpcomingBookingEmpty } from './upcoming/UpcomingBookingEmpty';
import { ServicesPreview } from './services/ServicesPreview';
import { SpecialistsSection } from './specialists/SpecialistsSection';
import { BusinessInfoSection } from './business-info/BusinessInfoSection';
import { ErrorRetry } from '../../../components/client/ui/ErrorRetry';
import { useHomeStrings } from '../hooks/useHomeStrings';

export const HomeScreen: React.FC = () => {
  const STRINGS = useHomeStrings();
  const navigate = useNavigate();
  const { data, isLoading, error, refresh } = useHomeData();
  const { isOnline } = useOnlineStatus();
  const resetFlow = useBookingFlowStore((s) => s.resetFlow);
  const providerCount = useClientTenantStore((s) => s.providerCount);
  const isMultiProvider = providerCount > 1;

  const handleBook = useCallback(() => {
    resetFlow();
    navigate('/booking/new/0');
  }, [resetFlow, navigate]);

  useMainButton(STRINGS.BOOK_NOW, handleBook, !!data && !isLoading);

  // Refresh home data when the WebApp becomes visible again
  // (e.g. user returns after pressing Android hardware back).
  const handleReactivate = useCallback(() => {
    void refresh();
  }, [refresh]);
  useRefreshOnVisibility(handleReactivate);

  const { containerRef, pullDistance, isRefreshing, progress } =
    usePullToRefresh<HTMLDivElement>({ onRefresh: refresh, disabled: isLoading });

  return (
    <div ref={containerRef} style={{ height: '100%', overflowY: 'auto', position: 'relative' }}>
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} progress={progress} />

      {isLoading ? <HomeScreenSkeleton />
      : error !== null ? <ErrorRetry message={error} onRetry={() => void refresh()} />
      : data === null ? null
      : (
        <div
          style={{
            paddingBottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
            background: 'var(--bl-bg)',
            minHeight: '100%',
          }}
        >
          {!isOnline && (
            <Alert
              type="warning"
              message={STRINGS.OFFLINE_BANNER}
              style={{ margin: '8px 16px', borderRadius: 8 }}
              showIcon
              banner
            />
          )}

          <BusinessHero business={data.business} />

          <div className="bl-animate-in bl-animate-in-delay-1">
            {data.upcomingBooking !== null ? (
              <UpcomingBookingCard booking={data.upcomingBooking} />
            ) : (
              <UpcomingBookingEmpty onBook={handleBook} />
            )}
          </div>

          <div className="bl-animate-in bl-animate-in-delay-2">
            {isMultiProvider ? (
              <SpecialistsSection />
            ) : (
              <ServicesPreview
                services={data.services}
                servicesTotal={data.servicesTotal}
                showPrice={data.business.showPrice}
              />
            )}
          </div>

          <div className="bl-animate-in bl-animate-in-delay-3">
            <BusinessInfoSection business={data.business} actions={data.actions} />
          </div>
        </div>
      )}
    </div>
  );
};
